/**
 * "Am I a good fit?" 30-second quiz modal.
 *
 * Scoring is 100% client-side for transparency — we post the *result* to the
 * backend only to (a) audit funnel conversion and (b) optionally email the
 * visitor their results via SendGrid. No email is required to see the result.
 *
 * Deep-links Sign-up to /register?plan={sema|tirz}-{low|mid|high} so the
 * pricing page pre-selects the right dose.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Mail, Sparkles, LineChart as LineChartIcon, Share2 } from "lucide-react";
import { toast } from "sonner";
import FitQuizProjection, { TWELVE_WEEK_PCT } from "./FitQuizProjection";

const API = process.env.REACT_APP_BACKEND_URL;

const GOALS = [
  { key: "lose_20",  label: "Lose 10–20 lbs",     weightFactor: 0 },
  { key: "lose_50",  label: "Lose 20–50 lbs",     weightFactor: 1 },
  { key: "lose_100", label: "Lose 50+ lbs",       weightFactor: 2 },
  { key: "maintain", label: "Maintain or manage cravings", weightFactor: 0 },
];

const CONDITIONS = [
  { key: "t2d",         label: "Type 2 diabetes" },
  { key: "prediabetes", label: "Prediabetes" },
  { key: "pcos",        label: "PCOS" },
  { key: "hypertension",label: "High blood pressure" },
  { key: "none",        label: "None of the above" },
];

/**
 * Scoring:
 *  - BMI >= 30  OR  BMI 27–30 with any qualifying condition → GREAT fit
 *  - BMI 27–30 no condition → GOOD fit
 *  - BMI 25–27 → BORDERLINE (recommend provider review)
 *  - BMI < 25  → NOT ideal (honest — don't oversell)
 *
 * Drug recommendation:
 *  - T2D/prediabetes OR goal 50+ lbs → Tirzepatide
 *  - otherwise → Semaglutide
 *
 * Dose (starting) — CLINICAL SAFETY RULE (FDA/ADA titration protocol):
 *  - If patient has NOT taken a GLP-1 in the last 2 weeks → MUST start Low.
 *    (GLP-1 tolerance resets after ~2 weeks; re-starting mid/high dose risks
 *    severe GI effects, pancreatitis.)
 *  - If last dose was ≤ 2 weeks ago → patient continues at their attested
 *    previous dose (they're still titrated in).
 *  - Otherwise default algorithmic pick based on BMI + goal.
 */
function computeResult({ heightIn, weightLb, goal, conditions, triedBefore, gapKey, previousDose }) {
  const bmi = heightIn > 0 ? (weightLb / (heightIn * heightIn)) * 703 : 0;
  const bmiR = Math.round(bmi * 10) / 10;
  const hasMetabolic = conditions.some((c) => ["t2d", "prediabetes", "pcos", "hypertension"].includes(c));

  let fit = "good";
  if (bmi >= 30) fit = "great";
  else if (bmi >= 27 && hasMetabolic) fit = "great";
  else if (bmi >= 27) fit = "good";
  else if (bmi >= 25) fit = "borderline";
  else if (bmi > 0)   fit = "not_ideal";

  // GLPFlo recommends Tirzepatide for every quiz taker — it's the newer dual-agonist
  // (GIP + GLP-1) and delivers stronger average weight loss in head-to-head trials.
  const drug = "tirz";

  // Safety gate first: if they've been off GLP-1 > 2 weeks (or never tried),
  // they MUST restart at Low regardless of BMI/goal.
  const mustRestartLow = !triedBefore || gapKey === "over_2_weeks";

  let doseTier = "low";
  let doseReason = "restart"; // "restart" | "continuation" | "algorithmic"

  if (mustRestartLow) {
    doseTier = "low";
    doseReason = triedBefore ? "restart" : "new_user";
  } else if (gapKey === "within_2_weeks" && ["low", "mid", "high"].includes(previousDose)) {
    // Continuation — honour their attested previous dose.
    doseTier = previousDose;
    doseReason = "continuation";
  } else {
    // Algorithmic fallback (shouldn't normally hit with gating above)
    if (bmi >= 35) doseTier = "high";
    else if (bmi >= 30) doseTier = "mid";
    doseReason = "algorithmic";
  }

  const plan = fit === "not_ideal" ? "" : `${drug}-${doseTier}`;

  return { bmi: bmiR, fit, recommended_plan: plan, dose_reason: doseReason };
}

const FIT_COPY = {
  great: {
    Icon: CheckCircle2,
    color: "text-emerald-700",
    badge: "bg-emerald-100 text-emerald-800",
    title: "You look like a great fit.",
    subtitle: "Your profile matches the people we help every day.",
  },
  good: {
    Icon: CheckCircle2,
    color: "text-emerald-700",
    badge: "bg-emerald-100 text-emerald-800",
    title: "You're a good fit.",
    subtitle: "A GLPFlo provider can review your history and tailor a plan.",
  },
  borderline: {
    Icon: AlertTriangle,
    color: "text-amber-700",
    badge: "bg-amber-100 text-amber-800",
    title: "You might be a fit — a provider should weigh in.",
    subtitle: "Your BMI is on the edge; our clinician will review your goals and any conditions.",
  },
  not_ideal: {
    Icon: XCircle,
    color: "text-rose-700",
    badge: "bg-rose-100 text-rose-800",
    title: "GLP-1 therapy may not be right for you yet.",
    subtitle: "We're honest: at your current BMI, lifestyle + your primary-care doctor may be a safer starting point.",
  },
};

const PLAN_LABELS = {
  "sema-low":  "Semaglutide · Low dose (0.25–0.5 mg/week · with B-12)",
  "sema-mid":  "Semaglutide · Mid dose (0.84–1.5 mg/week · with B-12)",
  "sema-high": "Semaglutide · High dose (2–2.4 mg/week · with B-12)",
  "tirz-low":  "Tirzepatide · Low dose (2.5 mg/week · with B-12)",
  "tirz-mid":  "Tirzepatide · Mid dose (5–7.5 mg/week · with B-12)",
  "tirz-high": "Tirzepatide · High dose (10–15 mg/week · with B-12)",
};

const STEPS = ["basics", "goal", "history", "conditions", "result"];

// Standard mg doses people are commonly on (matches tier ranges in PLAN_LABELS).
// Mapped to the existing low/mid/high tier so continuation logic stays
// identical — user just sees the actual mg they were on instead of "Low/Mid/High".
const SEMA_DOSE_OPTIONS = [
  { mg: "0.25", tier: "low"  },
  { mg: "0.5",  tier: "low"  },
  { mg: "1.0",  tier: "mid"  },
  { mg: "1.7",  tier: "mid"  },
  { mg: "2.4",  tier: "high" },
];
const TIRZ_DOSE_OPTIONS = [
  { mg: "2.5",  tier: "low"  },
  { mg: "5",    tier: "mid"  },
  { mg: "7.5",  tier: "mid"  },
  { mg: "10",   tier: "high" },
  { mg: "12.5", tier: "high" },
  { mg: "15",   tier: "high" },
];

export default function FitQuizModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Emit global open/close events so surrounding sticky banners
  // (e.g. OrdersPausedBanner) can quietly step aside for the modal.
  useEffect(() => {
    const evt = open ? "glpflo:modal-open" : "glpflo:modal-close";
    try { window.dispatchEvent(new CustomEvent(evt)); } catch { /* SSR */ }
    return () => {
      if (open) {
        try { window.dispatchEvent(new CustomEvent("glpflo:modal-close")); } catch { /* SSR */ }
      }
    };
  }, [open]);

  // Form state
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");
  const [weight, setWeight] = useState("");
  const [goal, setGoal] = useState("");
  const [triedBefore, setTriedBefore] = useState(null);
  const [gapKey, setGapKey] = useState("");             // "within_2_weeks" | "over_2_weeks"
  const [previousDrug, setPreviousDrug] = useState(""); // "sema" | "tirz"
  const [previousDoseMg, setPreviousDoseMg] = useState(""); // e.g. "0.25", "5"
  const [previousDose, setPreviousDose] = useState(""); // "low" | "mid" | "high" (derived)
  const [conditions, setConditions] = useState([]);

  // Email opt-in state
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showProjection, setShowProjection] = useState(false);
  const [sharing, setSharing] = useState(false);

  const heightIn = (Number(feet || 0) * 12) + Number(inches || 0);
  const weightLb = Number(weight || 0);

  const result = useMemo(
    () => computeResult({ heightIn, weightLb, goal, conditions, triedBefore: !!triedBefore, gapKey, previousDose }),
    [heightIn, weightLb, goal, conditions, triedBefore, gapKey, previousDose]
  );

  const reset = () => {
    setStep(0);
    setFeet(""); setInches(""); setWeight("");
    setGoal(""); setTriedBefore(null); setGapKey("");
    setPreviousDrug(""); setPreviousDoseMg(""); setPreviousDose("");
    setConditions([]);
    setEmailOpen(false); setEmail(""); setEmailing(false); setEmailSent(false);
    setSubmitted(false);
    setShowProjection(false);
  };

  const close = () => { onOpenChange(false); setTimeout(reset, 300); };

  const toggleCondition = (key) => {
    setConditions((prev) => {
      if (key === "none") return prev.includes("none") ? [] : ["none"];
      const next = prev.filter((c) => c !== "none");
      return next.includes(key) ? next.filter((c) => c !== key) : [...next, key];
    });
  };

  const canNext = () => {
    if (step === 0) return heightIn >= 48 && heightIn <= 90 && weightLb >= 80 && weightLb <= 700;
    if (step === 1) return !!goal;
    if (step === 2) {
      // Tried before? Gate: require gap answer when yes; require previous dose when gap is within_2_weeks.
      if (triedBefore === null) return false;
      if (triedBefore === false) return true;
      if (!gapKey) return false;
      if (gapKey === "within_2_weeks" && (!previousDrug || !previousDose)) return false;
      return true;
    }
    if (step === 3) return conditions.length > 0;
    return true;
  };

  const goNext = async () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      // Post the anonymous result on entering the result screen.
      if (step === 3 && !submitted) {
        setSubmitted(true);
        try {
          await axios.post(`${API}/api/quiz/submit`, {
            height_in: heightIn,
            weight_lb: weightLb,
            goal,
            conditions,
            tried_before: !!triedBefore,
            gap_key: gapKey || null,
            previous_drug: previousDrug || null,
            previous_dose_mg: previousDoseMg || null,
            previous_dose: previousDose || null,
            dose_reason: result.dose_reason,
            fit: result.fit,
            recommended_plan: result.recommended_plan,
            email: null,
          });
        } catch (_e) { /* non-blocking — UX still proceeds */ }
      }
    }
  };

  const handleSignup = () => {
    // Deep-link into the Royal Tier plan (best value) with the tirz dose the quiz picked.
    // Format matches PricingPage: `${drug_slug}-royal-${doseKey}` (e.g. "tirzepatide-royal-low").
    const suffix = result.recommended_plan
      ? `?plan=tirzepatide-royal-${result.recommended_plan.split("-")[1]}`
      : "";
    navigate(`/register${suffix}`);
    close();
  };

  /** Web Share API Level 2 — lets the visitor drop their projection into
   * Messages / iMessage / WhatsApp / Mail with one tap. We include the PNG
   * + a short caption + signup deep-link so friends who receive it land
   * on the exact recommended plan. Falls back to clipboard-copy of the
   * signup URL on unsupported browsers (Firefox desktop, older Chromes). */
  const handleShare = async () => {
    if (!result.recommended_plan) return;
    const [drug, dose] = result.recommended_plan.split("-");
    const origin = window.location.origin;
    const signupUrl = `${origin}/register?plan=${result.recommended_plan}&src=shared_projection`;
    const lossPct = TWELVE_WEEK_PCT[result.recommended_plan] || 0;
    const projectedLoss = Math.round(weightLb * (lossPct / 100) * 10) / 10;
    const caption = `My GLPFlo 90-day projection: ~${projectedLoss} lb off (${lossPct}%). Check if you're a fit too:`;

    setSharing(true);
    try {
      const resp = await fetch(`${API}/api/quiz/projection.png?weight_lb=${encodeURIComponent(weightLb)}&drug=${drug}&dose=${dose}`);
      if (!resp.ok) throw new Error("chart fetch failed");
      const blob = await resp.blob();
      const file = new File([blob], "glpflo-projection.png", { type: "image/png" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "My GLPFlo 90-day projection",
          text: `${caption} ${signupUrl}`,
          files: [file],
        });
        return;
      }
      // Fallback: text-only share
      if (navigator.share) {
        await navigator.share({ title: "GLPFlo", text: caption, url: signupUrl });
        return;
      }
      // Final fallback: copy to clipboard
      await navigator.clipboard.writeText(`${caption} ${signupUrl}`);
      toast.success("Copied share link — paste anywhere.");
    } catch (e) {
      if (e?.name === "AbortError") return; // user cancelled native sheet
      try {
        await navigator.clipboard.writeText(signupUrl);
        toast.success("Copied signup link — paste anywhere.");
      } catch (_c) {
        toast.error("Couldn't open share sheet. Please copy the link manually.");
      }
    } finally {
      setSharing(false);
    }
  };

  const sendResultsEmail = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setEmailing(true);
    try {
      const resp = await axios.post(`${API}/api/quiz/submit`, {
        height_in: heightIn,
        weight_lb: weightLb,
        goal,
        conditions,
        tried_before: !!triedBefore,
        gap_key: gapKey || null,
        previous_drug: previousDrug || null,
        previous_dose_mg: previousDoseMg || null,
        previous_dose: previousDose || null,
        dose_reason: result.dose_reason,
        fit: result.fit,
        recommended_plan: result.recommended_plan,
        email: trimmed,
      });
      if (resp.data?.email_sent) {
        toast.success("Results emailed — check your inbox.");
        setEmailSent(true);
      } else {
        toast.error("We couldn't send the email right now. You can still sign up directly below.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong sending the email.";
      toast.error(msg);
    } finally {
      setEmailing(false);
    }
  };

  const copy = FIT_COPY[result.fit] || FIT_COPY.good;
  const Icon = copy.Icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="fit-quiz-modal">
        <div className="bg-gradient-to-br from-purple-50 via-white to-violet-50 px-6 pt-5 pb-4 border-b border-purple-100 shrink-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Am I a good fit? · 30-second quiz
            </DialogTitle>
          </DialogHeader>
          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-purple-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-violet-600 transition-all duration-300"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              data-testid="fit-quiz-progress"
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">Step {step + 1} of {STEPS.length} · Not a medical diagnosis — a US-licensed provider reviews every signup.</p>
        </div>

        <div className="px-6 py-6 min-h-[280px] flex-1 overflow-y-auto">
          {/* Step 0: Basics */}
          {step === 0 && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-slate-900">Tell us your height &amp; weight</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Feet</Label>
                  <Input inputMode="numeric" type="number" min="4" max="7" value={feet} onChange={(e) => setFeet(e.target.value)} placeholder="5" data-testid="fit-quiz-feet" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Inches</Label>
                  <Input inputMode="numeric" type="number" min="0" max="11" value={inches} onChange={(e) => setInches(e.target.value)} placeholder="7" data-testid="fit-quiz-inches" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Weight (lb)</Label>
                  <Input inputMode="numeric" type="number" min="80" max="700" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="180" data-testid="fit-quiz-weight" />
                </div>
              </div>
              {heightIn >= 48 && weightLb >= 80 && (
                <p className="text-xs text-slate-500">Your BMI: <span className="font-semibold text-slate-800">{result.bmi}</span></p>
              )}
            </div>
          )}

          {/* Step 1: Goal */}
          {step === 1 && (
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-slate-900">What&apos;s your main goal?</h3>
              <div className="space-y-2">
                {GOALS.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => setGoal(g.key)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                      goal === g.key
                        ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200"
                        : "border-slate-200 hover:border-purple-300 hover:bg-purple-50/30"
                    }`}
                    data-testid={`fit-quiz-goal-${g.key}`}
                  >
                    <span className="text-sm font-medium text-slate-800">{g.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Tried before + (if yes) 2-week gap rule */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-slate-900">Have you tried a GLP-1 (Semaglutide / Ozempic, Wegovy · Tirzepatide / Mounjaro, Zepbound, etc.) before?</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: true,  label: "Yes, I have" },
                  { key: false, label: "No, first time" },
                ].map((o) => (
                  <button
                    key={String(o.key)}
                    type="button"
                    onClick={() => { setTriedBefore(o.key); if (o.key === false) { setGapKey(""); setPreviousDrug(""); setPreviousDoseMg(""); setPreviousDose(""); } }}
                    className={`px-4 py-3 rounded-xl border transition-all ${
                      triedBefore === o.key
                        ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200"
                        : "border-slate-200 hover:border-purple-300"
                    }`}
                    data-testid={`fit-quiz-tried-${o.key}`}
                  >
                    <span className="text-sm font-medium text-slate-800">{o.label}</span>
                  </button>
                ))}
              </div>

              {/* Follow-up 1: when was the last dose? */}
              {triedBefore === true && (
                <div className="space-y-2 pt-1">
                  <h4 className="text-sm font-semibold text-slate-900">When did you last take it?</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { key: "within_2_weeks", label: "Within the last 2 weeks" },
                      { key: "over_2_weeks",   label: "More than 2 weeks ago" },
                    ].map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => { setGapKey(o.key); if (o.key === "over_2_weeks") setPreviousDose(""); }}
                        className={`text-left px-4 py-2.5 rounded-xl border transition-all ${
                          gapKey === o.key
                            ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200"
                            : "border-slate-200 hover:border-purple-300"
                        }`}
                        data-testid={`fit-quiz-gap-${o.key}`}
                      >
                        <span className="text-sm font-medium text-slate-800">{o.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Follow-up 2a (within 2 weeks → attest previous drug + mg dose) */}
              {triedBefore === true && gapKey === "within_2_weeks" && (
                <div className="space-y-3 pt-1">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">Which medication?</h4>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {[
                        { key: "sema", label: "Semaglutide", brands: "Ozempic, Wegovy" },
                        { key: "tirz", label: "Tirzepatide", brands: "Mounjaro, Zepbound" },
                      ].map((d) => (
                        <button
                          key={d.key}
                          type="button"
                          onClick={() => {
                            setPreviousDrug(d.key);
                            // Reset dose when switching drug
                            setPreviousDoseMg("");
                            setPreviousDose("");
                          }}
                          className={`px-4 py-2.5 rounded-xl border text-center transition-all ${
                            previousDrug === d.key
                              ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200"
                              : "border-slate-200 hover:border-purple-300"
                          }`}
                          data-testid={`fit-quiz-prevdrug-${d.key}`}
                        >
                          <span className="block text-sm font-medium text-slate-800">{d.label}</span>
                          <span className="block text-xs text-slate-500 mt-0.5">({d.brands})</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {previousDrug && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">Which dose were you on?</h4>
                      <p className="text-xs text-slate-500">You&apos;ll be asked to confirm this again on your intake form — required for safe continuation.</p>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {(previousDrug === "sema" ? SEMA_DOSE_OPTIONS : TIRZ_DOSE_OPTIONS).map((o) => (
                          <button
                            key={o.mg}
                            type="button"
                            onClick={() => {
                              setPreviousDoseMg(o.mg);
                              setPreviousDose(o.tier);
                            }}
                            className={`px-3 py-2.5 rounded-xl border text-center transition-all ${
                              previousDoseMg === o.mg
                                ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200"
                                : "border-slate-200 hover:border-purple-300"
                            }`}
                            data-testid={`fit-quiz-prevdose-${previousDrug}-${o.mg}`}
                          >
                            <span className="text-sm font-medium text-slate-800">{o.mg} mg</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Follow-up 2b (over 2 weeks → explain the restart rule) */}
              {triedBefore === true && gapKey === "over_2_weeks" && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 leading-relaxed" data-testid="fit-quiz-restart-note">
                  <strong>Heads up:</strong> because it&apos;s been more than 2 weeks since your last dose,
                  your tolerance has reset. For your safety, you&apos;ll restart at the <strong>Low dose</strong>
                  and titrate up with your provider — this is standard clinical protocol.
                </div>
              )}
            </div>
          )}

          {/* Step 3: Conditions */}
          {step === 3 && (
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-slate-900">Any of these apply?</h3>
              <p className="text-xs text-slate-500">Select all that apply — or &quot;None&quot;. This helps us pick the right medication.</p>
              <div className="grid grid-cols-1 gap-2">
                {CONDITIONS.map((c) => {
                  const selected = conditions.includes(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => toggleCondition(c.key)}
                      className={`flex items-center gap-2 text-left px-4 py-2.5 rounded-xl border transition-all ${
                        selected
                          ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200"
                          : "border-slate-200 hover:border-purple-300"
                      }`}
                      data-testid={`fit-quiz-cond-${c.key}`}
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${selected ? "bg-purple-600 border-purple-600 text-white" : "border-slate-300"}`}>
                        {selected ? "✓" : ""}
                      </span>
                      <span className="text-sm text-slate-800">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 4 && (
            <div className="space-y-4" data-testid="fit-quiz-result">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${copy.badge}`}>
                  <Icon className={`w-5 h-5 ${copy.color}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${copy.color}`} data-testid="fit-quiz-headline">{copy.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{copy.subtitle}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
                <span>BMI <strong className="text-slate-900">{result.bmi}</strong></span>
                {result.recommended_plan && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span className="flex-1">Suggested: <strong className="text-slate-900">{PLAN_LABELS[result.recommended_plan]}</strong></span>
                  </>
                )}
              </div>

              {/* Flashy Royal Tier price block — headline offer for every quiz-taker.
                  Purple → fuchsia animated gradient, shimmer sweep, pulse-glow ring,
                  large tabular-nums price. Mirrors the hero CTA's visual language so
                  the CTA→signup path feels continuous. */}
              {result.recommended_plan && result.fit !== "not_ideal" && (
                <div
                  className="relative overflow-hidden rounded-2xl p-[2px] shadow-[0_10px_40px_-10px_rgba(147,51,234,0.55)]"
                  data-testid="fit-quiz-royal-price"
                  style={{
                    background: "linear-gradient(110deg, #9333ea 0%, #7c3aed 30%, #c026d3 55%, #ec4899 80%, #7c3aed 100%)",
                    backgroundSize: "220% 100%",
                    animation: "shimmer 3.2s linear infinite, pulse-glow 2.8s ease-in-out infinite",
                  }}
                >
                  <div className="relative rounded-[14px] bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 px-5 py-4 text-white overflow-hidden">
                    {/* Shimmer sweep */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background: "linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%)",
                        backgroundSize: "220% 100%",
                        animation: "shimmer 3.5s linear infinite",
                      }}
                    />
                    {/* Sparkles */}
                    <Sparkles aria-hidden="true" className="absolute top-2 right-3 w-4 h-4 text-fuchsia-200/80 animate-sparkle-float-slow" />
                    <Sparkles aria-hidden="true" className="absolute bottom-2 left-4 w-3 h-3 text-violet-200/80 animate-sparkle-spin" style={{ animationDelay: "0.6s" }} />

                    <div className="relative flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-fuchsia-100 backdrop-blur-sm">
                          <Sparkles className="w-3 h-3" /> Royal Tier · Best value
                        </span>
                        <p className="mt-1.5 text-xs text-purple-100/90 leading-tight">
                          3-month commitment · locks in your lowest price
                        </p>
                      </div>
                      <div className="text-right leading-none shrink-0 whitespace-nowrap">
                        <span className="block text-[10px] font-semibold uppercase tracking-wider text-purple-200/80 line-through">
                          $249/mo
                        </span>
                        <span className="block text-4xl font-black tabular-nums whitespace-nowrap bg-gradient-to-b from-white via-fuchsia-100 to-purple-200 bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(236,72,153,0.55)]">
                          $235
                        </span>
                        <span className="block text-[11px] font-semibold text-purple-100/90 -mt-0.5">
                          /month
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Subtle escape hatch to browse every dose/tier for shoppers who want the full picture. */}
              {result.recommended_plan && result.fit !== "not_ideal" && (
                <div className="text-center -mt-1">
                  <button
                    type="button"
                    onClick={() => { navigate("/pricing"); close(); }}
                    className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2"
                    data-testid="fit-quiz-see-all-pricing"
                  >
                    See all pricing
                  </button>
                </div>
              )}

              {/* Clinical-safety explainer — only when it's a restart/new user.
                  Helps set patient expectations before they hit the intake form. */}
              {result.recommended_plan && (result.dose_reason === "restart" || result.dose_reason === "new_user") && (
                <p className="text-xs text-slate-500 leading-relaxed" data-testid="fit-quiz-dose-reason">
                  {result.dose_reason === "restart"
                    ? "Because your last GLP-1 dose was more than 2 weeks ago, you'll restart at the Low dose — standard titration protocol."
                    : "First-time GLP-1 users always start at the Low dose and titrate up — it's the safest way to build tolerance."}
                </p>
              )}
              {result.recommended_plan && result.dose_reason === "continuation" && (
                <p className="text-xs text-slate-500 leading-relaxed" data-testid="fit-quiz-dose-reason">
                  Picking up where you left off — your provider will confirm your previous dose on the intake form.
                </p>
              )}

              {/* 90-day projection — only offered when we have a recommended plan.
                  Revealed on click so the result screen stays scannable by default. */}
              {result.recommended_plan && !showProjection && (
                <button
                  type="button"
                  onClick={() => setShowProjection(true)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-purple-200 bg-gradient-to-r from-purple-50 to-violet-50 px-4 py-3 text-sm text-purple-800 font-semibold hover:from-purple-100 hover:to-violet-100 transition-all group"
                  data-testid="fit-quiz-projection-toggle"
                >
                  <LineChartIcon className="w-4 h-4 text-purple-600" />
                  Show my 90-day projection
                  <ArrowRight className="w-3.5 h-3.5 text-purple-600 group-hover:translate-x-0.5 transition-transform" />
                </button>
              )}
              {result.recommended_plan && showProjection && (
                <FitQuizProjection
                  startWeight={weightLb}
                  drug={result.recommended_plan.split("-")[0]}
                  dose={result.recommended_plan.split("-")[1]}
                />
              )}

              {result.fit !== "not_ideal" ? (
                <>
                  <Button
                    onClick={handleSignup}
                    className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white py-5 text-base"
                    data-testid="fit-quiz-signup-btn"
                  >
                    Sign up now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  {result.recommended_plan && (
                    <Button
                      variant="outline"
                      onClick={handleShare}
                      disabled={sharing}
                      className="w-full rounded-full border-purple-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300 py-4 text-sm"
                      data-testid="fit-quiz-share-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      {sharing ? "Preparing…" : "Share my projection"}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  onClick={() => { navigate("/faq"); close(); }}
                  className="w-full rounded-full bg-slate-700 hover:bg-slate-800 text-white py-5 text-base"
                  data-testid="fit-quiz-read-faq-btn"
                >
                  Read our FAQ first
                </Button>
              )}

              {/* Optional email */}
              {!emailOpen ? (
                <button
                  type="button"
                  onClick={() => setEmailOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-purple-700 hover:text-purple-900 hover:underline underline-offset-4"
                  data-testid="fit-quiz-email-toggle"
                >
                  <Mail className="w-4 h-4" /> Email my results (optional)
                </button>
              ) : emailSent ? (
                <p className="text-center text-sm text-emerald-700 flex items-center justify-center gap-1.5" data-testid="fit-quiz-email-sent">
                  <CheckCircle2 className="w-4 h-4" /> Sent — check your inbox.
                </p>
              ) : (
                <div className="rounded-xl border border-purple-100 bg-purple-50/50 p-3 space-y-2" data-testid="fit-quiz-email-form">
                  <Label className="text-xs text-slate-700">We&apos;ll only use this to send your results.</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      data-testid="fit-quiz-email-input"
                    />
                    <Button
                      onClick={sendResultsEmail}
                      disabled={emailing || !email}
                      className="rounded-full bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap"
                      data-testid="fit-quiz-email-send-btn"
                    >
                      {emailing ? "Sending…" : "Send"}
                    </Button>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setEmailOpen(false); setEmail(""); }}
                    className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
                  >
                    Skip — no email
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer controls */}
        {step < STEPS.length - 1 && (
          <div className="px-6 pb-5 pt-2 flex items-center justify-between border-t border-slate-100 shrink-0">
            <Button
              type="button"
              variant="ghost"
              disabled={step === 0}
              onClick={() => setStep(Math.max(0, step - 1))}
              className="text-slate-500"
              data-testid="fit-quiz-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              type="button"
              onClick={goNext}
              disabled={!canNext()}
              className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="fit-quiz-next-btn"
            >
              {step === STEPS.length - 2 ? "See my results" : "Next"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
